<template>
    <div>
        <label v-show="hasLabel" class="block text-sm font-medium text-gray-700">{{ label }}</label>
        <div class="relative" :class="{
            'mt-1': hasLabel,
            'opacity-50 pointer-events-none': disabled
        }">
            <div ref="uploadZone" class="relative flex justify-center px-6 pt-5 pb-6 border-2 rounded-md" :class="{
                'border-solid border-teal': isDragover,
                'border-gray-300 border-dashed': !isDragover
            }">
                <div class="absolute top-0 left-0 z-10 flex items-center justify-center w-full h-full rounded-md bg-white bg-opacity-75" v-show="isBusy">
                    <svg class="animate-spin h-10 w-10 text-teal" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                </div>
                <div class="space-y-1 text-center ">
                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    <div class="flex text-sm text-gray-600">
                        <label :for="id" class="relative cursor-pointer bg-white rounded-md font-medium text-teal hover:text-teal focus-within:outline-none">
                            <span>Upload a file</span>
                            <input type="file" :id="id" multiple @change="handleUpload($event.target.files)" :accept="accept" :disabled="disabled" class="sr-only">
                        </label>
                        <p class="pl-1">or drag and drop</p>
                    </div>
                    <p class="text-xs text-gray-500">
                        {{ desc }}
                    </p>
                </div>
            </div>

            <ul v-if="uploadedFiles.length > 0 && showList" class="mt-4 border border-gray-200 rounded-md divide-y divide-gray-200">
                <li v-for="(file, index) in uploadedFiles" :key="index" class="pl-3 pr-4 py-3 flex items-center justify-between text-sm">
                    <div class="w-0 flex-1 flex items-center">
                        <svg class="flex-shrink-0 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M8 4a3 3 0 00-3 3v4a5 5 0 0010 0V7a1 1 0 112 0v4a7 7 0 11-14 0V7a5 5 0 0110 0v4a3 3 0 11-6 0V7a1 1 0 012 0v4a1 1 0 102 0V7a3 3 0 00-3-3z" clip-rule="evenodd"></path></svg>
                        <span class="ml-2 flex-1 w-0 truncate cursor-pointer hover:text-teal hover:underline">{{ file }}</span>
                    </div>
                    <div class="ml-4 flex-shrink-0 flex items-center">
                        <a href="#" @click.prevent="removeFile(index)" class="font-medium text-teal">Remove</a>
                    </div>
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import { onMounted, ref, watch } from 'vue';

export default {
    props: {
        label: {
            type: String,
            default: ''
        },
        id: {
            type: String,
            default(){
                return 'uploads-field-' + Math.random().toString(36).substr(2, 9);
            }
        },
        accept: {
            type: String,
            default: '.jpg,.bmp,.png,.pdf,.xls,.xlsx,.doc,.docx'
        },
        desc: {
            type: String,
            default: 'Accepts up to 10MB'
        },
        disabled: {
            type: Boolean,
            default: false
        },
        showList: {
            type: Boolean,
            default: true
        },
        modelValue: {
            type: Array,
            default: () => []
        }
    },
    emits: ['update:modelValue'],
    setup(props, { emit }){
        const isBusy = ref(false);
        const isDragover = ref(false);
        const uploadZone = ref(null);
        const uploadedFiles = (_.isArray(props.modelValue)) ? ref(props.modelValue) : ref([]);

        watch(() => props.modelValue, (value) => {
            uploadedFiles.value = value
        }, {
            immediate: true
        })

        async function handleUpload(files){
            if (files.length < 1) {
                return false;
            }

            isBusy.value = true;

            let invalid = []

            for (let index = 0; index < files.length; index++) {
                const formData = new FormData()
                const file = files[index]
                const size = file.size/1024/1024

                if (size > 10) {
                    invalid.push(file.name)
                    continue
                }

                formData.append('file', file)

                await axios.post(route('media.upload'), formData).then(response => {
                    if (response.status == 200) {
                        uploadedFiles.value.push(response.data.substr(response.data.lastIndexOf('/') + 1));

                        emit('update:modelValue', uploadedFiles.value);
                    }
                }).catch(error => console.log(error.response));
            }

            if (invalid.length > 0) {
                alert('Failed to upload ' + invalid.length + ' file(s) as it exceeds the maximum file size limit.')
            }

            isBusy.value = false;
        }

        function removeFile(index){
            let ask = confirm('Are you sure?');

            if (ask) {
                uploadedFiles.value.splice(index, 1);

                emit('update:modelValue', uploadedFiles.value);
            }
        }

        function preventDefaults(e){
            e.preventDefault();
            e.stopPropagation();
        }

        function highlight(){
            if (isBusy.value){
                return false;
            }

            isDragover.value = true;
        }

        function unhighlight(){
            if (isBusy.value){
                return false;
            }

            isDragover.value = false;
        }

        function handleDrop(e){
            let dt = e.dataTransfer
            let files = dt.files

            if (isBusy.value){
                return false;
            }

            if (files.length > 0) {
                handleUpload(files);
            }
        }

        onMounted(() => {
            ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
                uploadZone.value.addEventListener(eventName, preventDefaults, false);
            });

            ['dragenter', 'dragover'].forEach(eventName => {
                uploadZone.value.addEventListener(eventName, highlight, false);
            });

            ['dragleave', 'drop'].forEach(eventName => {
                uploadZone.value.addEventListener(eventName, unhighlight, false);
            });

            uploadZone.value.addEventListener('drop', handleDrop, false);
        });

        return {
            isBusy,
            uploadZone,
            uploadedFiles,
            handleUpload,
            removeFile,
            isDragover
        }
    },
    computed: {
        hasLabel(){
            return this.label != '';
        }
    }
}
</script>

<style>

</style>
